import { ref } from "vue";
import { ProgressModalData } from "./ProgressModal";

export enum ProgressState {
  NotStarted,
  Ongoing,
  Finished,
  Error,
  Cancelled,
  Cancelling
}

export function createIndeterminateProgress(id: string, title: string | null = null, isCancellable: boolean = false): ProgressModalData {
  const progress = ref<ProgressModalData>({
    id: id,
    isIndeterminate: true,
    isShow: false,
    title: title,
    isCancellable: isCancellable,
    state: ProgressState.NotStarted,
    max: null,
    min: null,
    value: null,
    withLabel: true
  });

  return progress.value;
}

export function createDeterminateProgress(
  id: string,
  title: string | null = null,
  min: number = 0,
  max: number = 100,
  isCancellable: boolean = false
): ProgressModalData {
  const progress = ref<ProgressModalData>({
    id: id,
    isIndeterminate: false,
    isShow: false,
    title: title,
    isCancellable: isCancellable,
    state: ProgressState.NotStarted,
    max: max,
    min: min,
    value: min,
    withLabel: true
  });

  return progress.value;
}

export class ProgressHandler {
  private internalProgress: ProgressModalData;
  private abortController?: AbortController | null;

  private constructor(progress: ProgressModalData) {
    this.internalProgress = progress;
    if (progress.isCancellable) {
      this.abortController = new AbortController();
    }
  }

  public static createIndeterminate(args: { id: string; title?: string | null; isCancellable?: boolean }) {
    const progress = createIndeterminateProgress(args.id, args.title ?? null, args.isCancellable ?? false);
    return new ProgressHandler(progress);
  }

  public static createDeterminate(args: { id: string; title?: string | null; min?: number; max?: number; isCancellable?: boolean }) {
    const progress = createDeterminateProgress(args.id, args.title ?? null, args.min ?? 0, args.max ?? 100, args.isCancellable ?? false);
    return new ProgressHandler(progress);
  }

  public static createForProgress(progress: ProgressModalData): ProgressHandler {
    return new ProgressHandler(progress);
  }

  public init(title?: string | null, message?: string | null): ProgressHandler {
    if (title !== undefined) {
      this.internalProgress.title = title;
    }

    if (message !== undefined) {
      this.internalProgress.message = message;
    }

    if (this.progress.isCancellable) {
      this.abortController = new AbortController();
    }

    this.internalProgress.startedAt = null;

    return this;
  }

  public get progress(): ProgressModalData {
    return this.internalProgress;
  }

  public get abortSignal(): AbortSignal | null {
    return this.abortController?.signal ?? null;
  }

  public get value(): number | null {
    return this.progress.value ?? null;
  }

  public title(title: string | null): ProgressHandler {
    this.internalProgress.title = title;
    return this;
  }

  public update(message: string | null): ProgressHandler {
    this.internalProgress.message = message;
    return this;
  }

  public progressTo(value: number): ProgressHandler {
    this.internalProgress.value = Math.min(this.progress.max ?? 100, Math.max(this.progress.min ?? 0, value));
    return this;
  }

  public start(initialMessage?: string, initialValue?: number): ProgressHandler {
    this.internalProgress.state = ProgressState.Ongoing;
    if (initialMessage !== undefined) {
      this.internalProgress.message = initialMessage;
    }

    this.internalProgress.value = initialValue;

    this.internalProgress.startedAt = new Date();
    return this;
  }

  public cancel(cancelMessage?: string | null): ProgressHandler {
    if (this.internalProgress.isCancellable) {
      if (this.abortController) {
        this.abortController.abort();
      }
      this.internalProgress.state = ProgressState.Cancelling;
      if (cancelMessage) {
        this.internalProgress.message = cancelMessage;
      }
    }

    return this;
  }

  public open(): ProgressHandler {
    this.internalProgress.isShow = true;
    return this;
  }

  public close(): ProgressHandler {
    this.internalProgress.isShow = false;
    return this;
  }

  public error(errorMessage: string, message?: string | null): ProgressHandler {
    this.internalProgress.errorMessage = errorMessage;
    this.internalProgress.state = ProgressState.Error;
    if (message !== undefined) {
      this.internalProgress.message = message;
    }

    return this;
  }

  public finish(finalMessage?: string | null): ProgressHandler {
    if (this.internalProgress.state === ProgressState.Cancelling) {
      this.internalProgress.state = ProgressState.Cancelled;
    } else if (this.internalProgress.state !== ProgressState.Cancelled && this.internalProgress.state !== ProgressState.Error) {
      this.internalProgress.state = ProgressState.Finished;
      this.internalProgress.message = finalMessage;
    }

    return this;
  }
}
